import { API_BASE } from '../../apiBase';
// src/projects/project_finances/ExpenseTab.js
import React, { useState, useRef, useEffect } from 'react';
import { Plus, Trash2, Upload, Check, X, Pencil, ChevronDown, FileText } from 'lucide-react';
import axios from 'axios';
import ReceiptCropper from './ReceiptCropper';

function ExpenseTab({ expenses, setExpenses, onRefresh, projectId, highlightExpenseId }) {
  // ---- Inline note editing: double-click (desktop) / double-tap (mobile) the
  // Notes cell. Optimistic: the text updates instantly, saves in background. ----
  const [noteEdit, setNoteEdit] = useState(null); // { id, val }
  const noteTapRef = useRef({ id: null, t: 0 });
  const startNoteEdit = (exp) => setNoteEdit({ id: exp._id, val: exp.notes || '' });
  const handleNoteTap = (exp, e) => {
    e.stopPropagation(); // a tap on the note must never open the receipt
    const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    if (!coarse) return; // desktop edits via double-click
    const now = Date.now();
    if (noteTapRef.current.id === exp._id && now - noteTapRef.current.t < 400) {
      noteTapRef.current = { id: null, t: 0 };
      startNoteEdit(exp);
    } else {
      noteTapRef.current = { id: exp._id, t: now };
    }
  };
  const commitNoteEdit = async () => {
    const ed = noteEdit;
    setNoteEdit(null);
    if (!ed) return;
    const exp = (expenses || []).find(x => x._id === ed.id);
    if (!exp) return;
    const v = ed.val.trim();
    if (v === (exp.notes || '')) return; // unchanged = no server call
    // INSTANT in the UI; server saves in the background.
    setExpenses && setExpenses(prev => (prev || []).map(x => x._id === ed.id ? { ...x, notes: v } : x));
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_BASE}/expenses/${ed.id}/notes`, { notes: v }, { headers: { Authorization: token } });
    } catch (_) {
      alert('Could not save the note — reverting.');
      onRefresh && onRefresh();
    }
  };
  const NoteCell = (exp, empty = '—') =>
    noteEdit && noteEdit.id === exp._id ? (
      <input
        autoFocus
        value={noteEdit.val}
        onChange={(e) => setNoteEdit(p => ({ ...p, val: e.target.value }))}
        onKeyDown={(e) => { if (e.key === 'Enter') commitNoteEdit(); if (e.key === 'Escape') setNoteEdit(null); }}
        onBlur={commitNoteEdit}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        className="bg-gray-800 border border-indigo-500/60 rounded px-2 py-1 text-sm text-white outline-none w-full min-w-0"
      />
    ) : (exp.notes || empty);

  // Briefly pulse + scroll to a specific expense row (used when arriving from
  // the Expense Ledger by clicking a PO).
  const [highlightId, setHighlightId] = useState(null);
  // Receipt-number key of the purchase to briefly highlight when a split-return
  // badge is clicked (the original purchase that split refunds).
  const [highlightPurchaseKey, setHighlightPurchaseKey] = useState(null);
  useEffect(() => {
    if (!highlightExpenseId || !expenses || expenses.length === 0) return;
    setHighlightId(highlightExpenseId);
    const tScroll = setTimeout(() => {
      const el = document.getElementById(`exp-row-${highlightExpenseId}`);
      if (!el) return;
      // Only scroll if the row isn't already fully in view.
      const rect = el.getBoundingClientRect();
      const inView = rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
      if (!inView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    const tClear = setTimeout(() => setHighlightId(null), 800);
    return () => { clearTimeout(tScroll); clearTimeout(tClear); };
  }, [highlightExpenseId, expenses]);
  // All state declarations first
  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
  const [showAddReceiptModal, setShowAddReceiptModal] = useState(false);
  const [showExtractionReview, setShowExtractionReview] = useState(false);
  const [batchData, setBatchData] = useState([]); // Array of extracted receipts
  const [pendingReceipts, setPendingReceipts] = useState([]);
  const [currentPendingId, setCurrentPendingId] = useState(null);
  const [editingBatchId, setEditingBatchId] = useState(null); // which review row is expanded for editing

  // Edit a field on a batch receipt and live-recompute the HARD validation
  // (subtotal+tax==total, charged==total) so the ⚠️ updates as you fix it.
  const updateBatchField = (id, field, value) => {
    setBatchData(prev => prev.map(r => {
      if (r.id !== id) return r;
      const next = { ...r, [field]: value };
      const sub = Number(next.subtotal) || 0;
      const tax = Number(next.taxAmount) || 0;
      const total = Number(next.totalAmount) || 0;
      const charged = Number(next.chargedAmount) || 0;
      const issues = [];
      if (sub > 0 && total > 0 && Math.abs((sub + tax) - total) > 0.03)
        issues.push(`subtotal+tax (${(sub + tax).toFixed(2)}) != total (${total.toFixed(2)})`);
      if (charged > 0 && total > 0 && Math.abs(charged - total) > 0.02)
        issues.push(`charged (${charged.toFixed(2)}) != total (${total.toFixed(2)})`);
      next.needsReview = issues.length > 0;
      next.reviewReason = issues.join('; ') || null;
      return next;
    }));
  };

  // Open the physical receipt file for a pending batch item in a new tab. Fetches
  // with auth into a blob (so PDFs/images render inline) — same pattern the
  // Ledger uses for Dropbox files, but pointed at the not-yet-saved temp file.
  // Open a SAVED expense's receipt. For a multi-document packet (Rexel
  // Acknowledgement + Ship Ticket, kept as separate files in Dropbox) the server
  // merges them into ONE PDF at view time, so a single click brings up the whole
  // packet as one blob. Falls back to the single file if the combined route fails.
  const viewSavedReceipt = async (exp) => {
    if (!exp || (!exp._id && !exp.receiptImagePath)) return;
    const token = localStorage.getItem('token');
    // iOS Safari blocks window.open() that runs AFTER an await (it's no longer
    // tied to the tap). So open the tab synchronously NOW, then point it at the
    // blob once the fetch finishes. On desktop this behaves the same.
    const win = window.open('', '_blank');
    const finish = (url) => {
      if (win) { win.location = url; }
      else { window.location.href = url; } // popup blocked entirely — navigate current tab
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    };
    const fail = (msg) => { if (win) win.close(); alert(msg); };

    if (exp._id) {
      try {
        const resp = await axios.get(
          `${API_BASE}/expenses/${exp._id}/combined-pdf`,
          { headers: { Authorization: token }, responseType: 'blob' }
        );
        finish(URL.createObjectURL(resp.data));
        return;
      } catch (err) {
        console.error('Combined receipt open failed, falling back:', err);
      }
    }
    // Fallback: open the single primary file directly.
    if (!exp.receiptImagePath) { fail('Could not open the receipt file.'); return; }
    try {
      const linkRes = await axios.get(
        `${API_BASE}/projects/files/link?path=${encodeURIComponent(exp.receiptImagePath)}`,
        { headers: { Authorization: token } }
      );
      const fileResp = await fetch(linkRes.data.link);
      const rawBlob = await fileResp.blob();
      const isPdf = exp.receiptImagePath.toLowerCase().endsWith('.pdf');
      const finalBlob = new Blob([rawBlob], { type: isPdf ? 'application/pdf' : 'image/jpeg' });
      finish(URL.createObjectURL(finalBlob));
    } catch (err) {
      console.error('Open receipt failed', err);
      fail('Could not open the receipt file.');
    }
  };

  const viewBatchReceipt = async (res) => {
    if (!currentPendingId) return;
    // Open the tab synchronously so iOS Safari doesn't block it after the await.
    const win = window.open('', '_blank');
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(
        `${API_BASE}/expenses/pending/${currentPendingId}/receipt/${res.id}`,
        { headers: { Authorization: token }, responseType: 'blob' }
      );
      const url = URL.createObjectURL(resp.data);
      if (win) { win.location = url; } else { window.location.href = url; }
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch (err) {
      console.error('View receipt failed', err);
      if (win) win.close();
      alert('Could not open the receipt file.');
    }
  };
const [selectedReceipts, setSelectedReceipts] = useState([]); // Array of IDs/Indices
const [selectedRows, setSelectedRows] = useState([]);
const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
const [isUploading, setIsUploading] = useState(false); // New state for immediate UI feedback
const [isSaving, setIsSaving] = useState(false); // Prevents double-clicking the save button
const [showSuccessToast, setShowSuccessToast] = useState(false);
const [saveError, setSaveError] = useState(null);
// Watcher: Auto-transition from Loading screen to Review screen
  useEffect(() => {
    const readyBatch = pendingReceipts.find(p => p.status === 'Ready');
    
    // If we have a ready batch AND the user is still staring at the upload modal...
    if (readyBatch && showAddReceiptModal) {
      // 1. Close the drag and drop/loading modal
      setShowAddReceiptModal(false);
      
      // 2. Load the data
      setBatchData(readyBatch.extractedData);
      setSelectedReceipts(readyBatch.extractedData.map(r => r.id));
      setCurrentPendingId(readyBatch._id);
      
      // 3. Pop open the Review screen automatically!
      setShowExtractionReview(true);
    }
  }, [pendingReceipts, showAddReceiptModal]);

  // Failure watcher: if the server-side scan errored, SAY SO (previously the
  // spinner just stopped silently) and clear the dead batch so it doesn't
  // haunt the polling forever.
  useEffect(() => {
    const failed = pendingReceipts.filter(p => p.status === 'Failed');
    if (failed.length === 0) return;
    // Show the ACTUAL reason the worker recorded (e.g. a bad API key, an
    // unreadable image) instead of a generic message, so silent fails aren't.
    const reason = failed.map(p => p.failureReason).filter(Boolean)[0];
    setSaveError(
      reason
        ? `Receipt scan failed: ${reason}`
        : 'Receipt scan failed on the server — nothing was saved. Please try again; if it keeps happening, check the backend logs.'
    );
    setShowAddReceiptModal(false);
    const token = localStorage.getItem('token');
    failed.forEach(p => {
      axios.delete(`${API_BASE}/expenses/pending/${p._id}`, { headers: { Authorization: token } }).catch(() => {});
    });
    setPendingReceipts(prev => prev.filter(p => p.status !== 'Failed'));
  }, [pendingReceipts]);


  // LOCAL today (toISOString is UTC — after ~5pm Pacific it's already tomorrow)
  const localToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const [newExpense, setNewExpense] = useState({
    date: localToday(),
    store: 'Home Depot',
    receiptNumber: '',
    category: 'Materials',
    notes: '',
    taxRate: '7.75',
    manualTotal: '', // grand total when there's no itemized receipt (tax back-calculated from the rate)
    // Labor-only fields
    laborType: 'Lath & Stucco',
    payee: '',
    paymentMethod: 'Cash',
    laborAmount: '',
  });

  // Common trades for the Labor category (plus free-text "Other").
  const LABOR_TYPES = ['Lath & Stucco', 'Lath', 'Stucco', 'Carpentry', 'Drywall',
    'Painting', 'Concrete', 'Fire Alarm', 'Security System', 'Low Voltage',
    'HVAC', 'Plumbing', 'Demolition', 'Trenching', 'Cleanup', 'General Labor', 'Other'];
  const LABOR_PAYMENT_METHODS = ['Cash', 'Check', 'Venmo', 'Cash App', 'Zelle', 'Card', 'Other'];
  const isLabor = newExpense.category === 'Labor';

  const [items, setItems] = useState([{ id: 1, name: '', price: '' }]);

  // ==================== HELPER: Build Dropbox Folder Path ====================
  const buildDropboxFolderPath = async (projectId) => {
    const token = localStorage.getItem('token');
    const projectRes = await axios.get(`${API_BASE}/projects/${projectId}`, {
      headers: { Authorization: token }
    });
    const project = projectRes.data;

    const po = String(project.poNumber || '0000').padStart(4, '0');
    let street = (project.jobSiteAddress?.street || '').trim();
    const houseNum = street.match(/^\d+/) ? street.match(/^\d+/)[0] : '';
    let streetName = street.replace(/^\d+\s*/, '').replace(/[^a-zA-Z0-9]/g, '').replace(/\s+/g, '');
    const city = (project.jobSiteAddress?.city || 'Unknown').replace(/[^a-zA-Z0-9]/g, '').replace(/\s+/g, '');

    const projectFolderName = `${po}_${houseNum ? houseNum + '_' : ''}${streetName}_${city}`;
    return `/2026/Projects/${projectFolderName}/AccountsPayable/VendorReceipts`;
  };

  

const handleSaveBatch = async () => {
  if (isSaving) return; // Failsafe: If already saving, ignore any extra clicks
  setIsSaving(true);    // Lock the button instantly

  try {
    const token = localStorage.getItem('token');
    
    // 1. Prepare data to save
    const toSave = batchData
      .filter(r => selectedReceipts.includes(r.id))
      .map(r => ({
        ...r,
        project: projectId,
        // Keep subtotal and total distinct (the backend stores both). amount =
        // pre-tax subtotal; totalAmount = grand total charged.
        amount: Number(r.subtotal != null ? r.subtotal : (r.totalAmount || 0)),
        subtotal: Number(r.subtotal != null ? r.subtotal : (r.totalAmount || 0)),
        totalAmount: Number(r.totalAmount || 0),
        category: r.category || (r.store === 'Permit' ? 'Permits' : 'Materials'),
      }));

    // ✨ NEW: Calculate the leftovers (the ones you left unchecked!)
    const leftovers = batchData.filter(r => !selectedReceipts.includes(r.id));

    if (toSave.length === 0) return alert("No receipts selected.");

    // 2. Send EVERYTHING to the backend in one clean shot
    await axios.post(`${API_BASE}/expenses/batch`, { 
      expenses: toSave,
      pendingId: currentPendingId, // Pass the ID
      leftovers: leftovers         // Pass the unchecked items
    }, {
      headers: { Authorization: token }
    });

    // 3. ✨ NEW: Update UI State dynamically based on leftovers
    if (leftovers.length > 0) {
      // If there are leftovers, just update the local state so the button stays alive
      setPendingReceipts(prev => 
        prev.map(p => p._id === currentPendingId ? { ...p, extractedData: leftovers } : p)
      );
    } else {
      // If no leftovers, wipe it completely
      setPendingReceipts(prev => prev.filter(p => p._id !== currentPendingId));
    }

    // 4. Reset everything else
    setCurrentPendingId(null);
    setShowExtractionReview(false);
    setBatchData([]);
    setSelectedReceipts([]);
    if (onRefresh) onRefresh();
    // Saving may resolve this batch's notification — refresh the bell now.
    window.dispatchEvent(new Event('notifications:refresh'));
    
    // 5. Trigger the beautiful 3-second toast notification
    setShowSuccessToast(true);
    setTimeout(() => {
      setShowSuccessToast(false);
    }, 3000);
  } catch (err) {
    console.error("Save failed:", err);
    setSaveError(`Failed to save: ${err.response?.data?.error || "Check terminal"}`);
  } finally {
    setIsSaving(false); // Unlock the button
  }
};

const handleDiscardBatch = async () => {
  if (currentPendingId) {
    const token = localStorage.getItem('token');
    await axios.delete(`${API_BASE}/expenses/pending/${currentPendingId}`, {
      headers: { Authorization: token }
    });
    setCurrentPendingId(null);
  }
  setShowExtractionReview(false);
  setBatchData([]);
  window.dispatchEvent(new Event('notifications:refresh'));
};

const toggleLink = (id) => {
  setBatchData(prev => {
    const item = prev.find(r => r.id === id);
    if (item.linkId) {
      // If it's already linked, remove the linkId from this specific one
      return prev.map(r => r.id === id ? { ...r, linkId: null } : r);
    } else {
      // If it's not linked, give it a temporary ID to pair with another
      const newId = `manual-${Date.now()}`;
      return prev.map(r => r.id === id ? { ...r, linkId: newId } : r);
    }
  });
};

const handleManualLink = (id, targetLinkId) => {
  setBatchData(prev => prev.map(r => r.id === id ? { ...r, linkId: targetLinkId } : r));
};

// ==================== INBOX POLLING ====================
// This quietly checks the server every 5 seconds to see if receipts are done
useEffect(() => {
  let interval;
  const checkPending = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/expenses/pending/${projectId}`, {
        headers: { Authorization: token }
      });
      setPendingReceipts(res.data);
    } catch (err) {
      console.error("Failed to fetch pending receipts");
    }
  };

  checkPending(); // Check immediately on load
  interval = setInterval(checkPending, 5000); // Check every 5 seconds

  return () => clearInterval(interval);
}, [projectId]);

useEffect(() => {
  const handleKeyDown = (e) => {
    // Check if the Delete key or Backspace key is pressed
    if (e.key === 'Delete' || e.key === 'Backspace') {
      // Only trigger if rows are selected and we aren't typing in an input field
      if (selectedRows.length > 0 && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();
        handleDeleteSelected();
      }
    }
  };

  window.addEventListener('keydown', handleKeyDown);
  return () => window.removeEventListener('keydown', handleKeyDown);
}, [selectedRows]); // Re-run whenever the selection changes
const handleRowClick = (e, index, receiptId) => {
  let newSelected = [...selectedRows];

  if (e.shiftKey && lastSelectedIndex !== null) {
    // Range selection
    const start = Math.min(lastSelectedIndex, index);
    const end = Math.max(lastSelectedIndex, index);
    const rangeIds = expenses.slice(start, end + 1).map(exp => exp._id);
    newSelected = [...new Set([...newSelected, ...rangeIds])];
  } else if (e.metaKey || e.ctrlKey) {
    // Command/Ctrl + Click to toggle individual items
    if (newSelected.includes(receiptId)) {
      newSelected = newSelected.filter(id => id !== receiptId);
    } else {
      newSelected.push(receiptId);
    }
  } else {
    // Single click
    newSelected = [receiptId];
  }

  setSelectedRows(newSelected);
  setLastSelectedIndex(index);
};

// Clear selection when clicking outside the table
useEffect(() => {
  const handleClickOutside = (e) => {
    const tableContainer = document.querySelector('.expenses-table-container');
    if (tableContainer && !tableContainer.contains(e.target)) {
      setSelectedRows([]);
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, []);

const handleDeleteSelected = async () => {
  if (!selectedRows.length || !window.confirm(`Delete ${selectedRows.length} selected expenses?`)) return;
  try {
    const token = localStorage.getItem('token');
    await Promise.all(selectedRows.map(id => axios.delete(`${API_BASE}/expenses/${id}`, { headers: { Authorization: token } })));
    setSelectedRows([]);
    onRefresh();
  } catch (err) {
    console.error("Delete Error:", err);
  }
};

// ==================== ABORT CONTROLLERS FOR CANCELLATION ====================
const abortControllersRef = useRef([]);

// ==================== NON-BLOCKING UPLOAD ====================
const handleUploadReceipts = async (files) => {
  if (!files || files.length === 0) return;

  setIsUploading(true); // Start the local loading state immediately
  const token = localStorage.getItem('token');

  try {
    const folderPath = await buildDropboxFolderPath(projectId);
    const formData = new FormData();
    Array.from(files).forEach(file => formData.append('file', file));
    formData.append('projectId', projectId);
    formData.append('folderPath', folderPath);

    const res = await axios.post(
      `${API_BASE}/expenses/extract-receipt`,
      formData,
      { headers: { 'Authorization': token, 'Content-Type': 'multipart/form-data' } }
    );

    // We store the ID so we know which specific batch we are watching
    setCurrentPendingId(res.data.pendingId);
    
    // Clear the file input
    setReceiptFiles([]); 
    
    // Bridge the gap: instantly tell the UI we are processing so it doesn't flicker
    setPendingReceipts(prev => [...prev, { _id: res.data.pendingId, status: 'Processing' }]);
    setIsUploading(false);

  } catch (err) {
    console.error("Upload failed:", err);
    alert("Upload failed.");
    setIsUploading(false);
  }
};

// ==================== CANCEL HANDLER ====================
const handleCancelUpload = () => {
  // Abort any in-flight requests
  abortControllersRef.current.forEach(controller => controller.abort());
  abortControllersRef.current = [];

  // Reset everything
  setReceiptFiles([]);                    // ← Clear pending files
  setShowAddReceiptModal(false);
  setUploadingReceipt(false);
};
  // Receipt Upload + Extraction
  const [receiptFiles, setReceiptFiles] = useState([]); // Changed from receiptFile to receiptFiles

  // ---- Scan/crop step ----
  // Selected IMAGES queue through the cropper one at a time (auto corner
  // detection → drag to adjust → perspective-corrected). PDFs skip it.
  const [cropQueue, setCropQueue] = useState([]);
  const enqueueReceiptFiles = (files) => {
    const list = Array.from(files || []);
    const images = list.filter(f => f.type.startsWith('image/') || /\.(heic|heif|jpe?g|png|webp)$/i.test(f.name || ''));
    const others = list.filter(f => !images.includes(f));
    if (others.length) setReceiptFiles(prev => [...prev, ...others]);
    if (images.length) setCropQueue(prev => [...prev, ...images]);
  };
  const [isDragging, setIsDragging] = useState(false);
  // Hidden easter egg: drag files straight onto the expenses table to scan them
  // (same flow as the Add Receipt button). No persistent UI hint by design.
  const [tableDragging, setTableDragging] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [extractionLoading, setExtractionLoading] = useState(false);

  const [isItemMode, setIsItemMode] = useState(false);

  // --- MISSING HANDLERS TO ADD ---
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(file => 
      file.type.startsWith('image/') || file.type === 'application/pdf'
    );
    setReceiptFiles(prev => [...prev, ...validFiles]);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setReceiptFiles(prev => [...prev, ...files]);
    }
  };

  // Refs
  const nameRefs = useRef([]);
  const priceRefs = useRef([]);

  // ==================== MANUAL EXPENSE HELPERS ====================
  useEffect(() => {
    if (isItemMode && nameRefs.current[0]) {
      setTimeout(() => nameRefs.current[0]?.focus(), 50);
    }
  }, [isItemMode]);

  const formatPrice = (value) => {
    let cleaned = value.replace(/[^0-9.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length > 2) cleaned = parts[0] + '.' + parts.slice(1).join('');
    if (parts[0]) {
      const num = parseFloat(parts[0]);
      if (!isNaN(num)) {
        const integerPart = num.toLocaleString('en-US');
        cleaned = integerPart + (parts[1] !== undefined ? '.' + parts[1] : '');
      }
    }
    return cleaned;
  };

  const formatTaxRate = (value) => value.replace(/[^0-9.]/g, '');

  const addNewItemRow = () => {
    const cleanedItems = items.filter(item => item.name.trim() !== '' || item.price.trim() !== '');
    setItems([...cleanedItems, { id: Date.now(), name: '', price: '' }]);
    setTimeout(() => nameRefs.current[cleanedItems.length]?.focus(), 10);
  };

  const updateItem = (id, field, value) => {
    if (field === 'price') {
      setItems(items.map(item => item.id === id ? { ...item, price: formatPrice(value) } : item));
    } else {
      setItems(items.map(item => item.id === id ? { ...item, [field]: value } : item));
    }
  };

  const deleteItem = (id) => {
    if (items.length === 1) return;
    setItems(items.filter(item => item.id !== id));
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + (parseFloat(item.price.replace(/,/g, '')) || 0), 0);
  };

  const itemsSubtotal = calculateSubtotal();
  const taxRateNum = parseFloat(newExpense.taxRate) || 0;
  const manualTotalNum = parseFloat(String(newExpense.manualTotal || '').replace(/,/g, '')) || 0;
  // Itemized entries → compute forward (subtotal → +tax → total). No receipt, just a
  // known grand total → compute in reverse (total → split the tax back out).
  let subtotal, taxAmount, totalAmount;
  if (itemsSubtotal > 0) {
    subtotal = itemsSubtotal;
    taxAmount = (subtotal * (taxRateNum / 100)).toFixed(2);
    totalAmount = (subtotal + parseFloat(taxAmount)).toFixed(2);
  } else if (manualTotalNum > 0) {
    subtotal = manualTotalNum / (1 + taxRateNum / 100);
    taxAmount = (manualTotalNum - subtotal).toFixed(2);
    totalAmount = manualTotalNum.toFixed(2);
  } else {
    subtotal = 0; taxAmount = '0.00'; totalAmount = '0.00';
  }

  const handleDoneWithItems = () => {
    const cleanedItems = items.filter(item => item.name.trim() !== '' || item.price.trim() !== '');
    setItems(cleanedItems.length > 0 ? cleanedItems : [{ id: 1, name: '', price: '' }]);
    setIsItemMode(false);
  };

  const resetNewExpense = () => setNewExpense({
    date: localToday(),
    store: 'Home Depot',
    receiptNumber: '',
    category: 'Materials',
    notes: '',
    taxRate: '7.75',
    manualTotal: '',
    laborType: 'Lath & Stucco',
    payee: '',
    paymentMethod: 'Cash',
    laborAmount: '',
  });

  const handleAddExpense = async () => {
    const token = localStorage.getItem('token');

    // ---- LABOR: no store / receipt / tax — just trade, payee, amount, method ----
    if (isLabor) {
      const amt = parseFloat(String(newExpense.laborAmount).replace(/,/g, ''));
      if (!amt || amt <= 0) { alert('Enter the amount paid.'); return; }
      try {
        await axios.post(`${API_BASE}/expenses`, {
          project: projectId,
          date: newExpense.date,
          category: 'Labor',
          laborType: newExpense.laborType,
          payee: newExpense.payee,
          paymentMethod: newExpense.paymentMethod,
          // Use the trade as the "vendor" so the Ledger reads cleanly.
          store: newExpense.payee ? `${newExpense.laborType} — ${newExpense.payee}` : newExpense.laborType,
          receiptNumber: '',
          amount: amt,
          subtotal: amt,
          taxAmount: 0,
          totalAmount: amt,
          documentType: 'manual_labor',
          notes: newExpense.notes || newExpense.laborType,
        }, { headers: { Authorization: token } });
        await onRefresh();
        setShowAddExpenseModal(false);
        setIsItemMode(false);
        resetNewExpense();
        setItems([{ id: 1, name: '', price: '' }]);
      } catch (err) {
        console.error(err);
        alert('Failed to save labor expense.');
      }
      return;
    }

    // ---- STANDARD (materials / tools / etc.) ----
    if (!newExpense.notes) {
      alert("Please add a note.");
      return;
    }
    if (parseFloat(totalAmount) <= 0) {
      alert("Enter the total charged, or use Enter Items to itemize.");
      return;
    }

    try {
      await axios.post(`${API_BASE}/expenses`, {
        project: projectId,
        date: newExpense.date,
        store: newExpense.store,
        receiptNumber: newExpense.receiptNumber,
        category: newExpense.category,
        amount: parseFloat(totalAmount),
        taxAmount: parseFloat(taxAmount),
        totalAmount: parseFloat(totalAmount),
        notes: newExpense.notes,
      }, { headers: { Authorization: token } });

      await onRefresh();
      setShowAddExpenseModal(false);
      setIsItemMode(false);
      resetNewExpense();
      setItems([{ id: 1, name: '', price: '' }]);
    } catch (err) {
      console.error(err);
      alert("Failed to save expense.");
    }
  };

 // Save after review (backend already saved + linked the expense)
const handleSaveExtractedExpense = async () => {
  try {
    await onRefresh();  // Refresh the table to show newly saved expenses
    alert("Receipts saved successfully!");
    setShowAddReceiptModal(false);
    setShowExtractionReview(false);
    setReceiptFiles(null);
    setExtractedData(null);
  } catch (err) {
    console.error(err);
    alert("Failed to refresh expenses.");
  }
};

  // Right-click delete
  const [contextMenu, setContextMenu] = useState(null);

  // ---- Split an expense: tick line items (or type an amount) → move them to
  // another project or an Overhead category. Tax prorates automatically.
  const [splitExp, setSplitExp] = useState(null);      // expense being split
  const [splitPicks, setSplitPicks] = useState([]);    // selected item indexes
  const [splitManual, setSplitManual] = useState({ amount: '', label: '' });
  const [splitDestQ, setSplitDestQ] = useState('');
  const [splitDest, setSplitDest] = useState(null);    // { kind:'project'|'overhead', id, label }
  const [splitTargets, setSplitTargets] = useState({ projects: [], cats: [] });
  const [splitBusy, setSplitBusy] = useState(false);
  const [splitHist, setSplitHist] = useState(null); // { children, parent } — prior surgery on this receipt
  const openSplit = async (expenseId) => {
    const exp = (expenses || []).find((x) => x._id === expenseId);
    if (!exp) return;
    setContextMenu(null);
    setSplitExp(exp); setSplitPicks([]); setSplitManual({ amount: '', label: '' });
    setSplitDestQ(''); setSplitDest(null); setSplitHist(null);
    try {
      const token = localStorage.getItem('token');
      const [pr, cr, hist] = await Promise.all([
        axios.get(`${API_BASE}/projects`, { headers: { Authorization: token } }).then(r => r.data || []).catch(() => []),
        axios.get(`${API_BASE}/cost-categories`, { headers: { Authorization: token } }).then(r => r.data || []).catch(() => []),
        axios.get(`${API_BASE}/expenses/${expenseId}/splits`, { headers: { Authorization: token } }).then(r => r.data).catch(() => null),
      ]);
      setSplitTargets({ projects: pr, cats: cr });
      setSplitHist(hist);
    } catch (_) {}
  };
  const splitDestOf = (x) => {
    if (x?.project) {
      const po = x.project.poNumber || (typeof x.project === 'string' ? x.project : '—');
      return `PO ${po}${x.project.title ? ` — ${x.project.title}` : ''}`;
    }
    if (x?.costCategory) return `Overhead/${x.costCategory.name || x.costCategory.code || 'Other'}`;
    return 'unassigned';
  };
  // ---- Edit Items: hand-correct lines the AI couldn't read off a scan.
  // Booked totals never change here — items are the detail beneath them.
  const [itemsExp, setItemsExp] = useState(null);   // expense being corrected
  const [itemRows, setItemRows] = useState([]);
  const [itemsBusy, setItemsBusy] = useState(false);
  const openEditItems = (expenseId) => {
    const exp = (expenses || []).find((x) => x._id === expenseId);
    if (!exp) return;
    setContextMenu(null);
    setItemsExp(exp);
    setItemRows((exp.items && exp.items.length)
      ? exp.items.map((it) => ({ name: it.name || '', sku: it.sku || '', quantity: it.quantity ?? 1, unitPrice: it.unitPrice ?? '', total: it.total ?? '' }))
      : [{ name: '', sku: '', quantity: 1, unitPrice: '', total: '' }]);
  };
  const setItemRow = (i, f, v) => setItemRows((rows) => rows.map((r, j) => {
    if (j !== i) return r;
    const next = { ...r, [f]: v };
    // qty/unit changes keep total in step unless the user edits total directly
    if (f === 'quantity' || f === 'unitPrice') {
      const q = Number(f === 'quantity' ? v : next.quantity) || 0;
      const u = Number(f === 'unitPrice' ? v : next.unitPrice) || 0;
      if (q && u) next.total = (Math.round(q * u * 100) / 100).toString();
    }
    return next;
  }));
  const itemRowsSum = itemRows.reduce((s, r) => s + (Number(r.total) || 0), 0);
  const saveItems = async () => {
    if (itemsBusy || !itemsExp) return;
    setItemsBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/expenses/${itemsExp._id}/items`, {
        items: itemRows.filter((r) => r.name.trim()).map((r) => ({
          name: r.name, sku: r.sku, quantity: Number(r.quantity) || 1,
          unitPrice: Number(r.unitPrice) || 0, total: Number(r.total) || 0,
        })),
      }, { headers: { Authorization: token } });
      setItemsExp(null);
      await onRefresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not save the items.');
    } finally {
      setItemsBusy(false);
    }
  };

  const splitItems = Array.isArray(splitExp?.items) ? splitExp.items : [];
  const splitPickSum = splitPicks.reduce((s, i) => s + Number(splitItems[i]?.total || 0), 0);
  const splitPreTax = splitItems.length ? splitPickSum : Number(splitManual.amount || 0);
  const splitTaxShare = splitExp && Number(splitExp.amount) > 0
    ? Math.round(Number(splitExp.taxAmount || 0) * (splitPreTax / Number(splitExp.amount)) * 100) / 100
    : 0;
  const splitMatches = splitDestQ.trim().length >= 1 && !splitDest ? [
    ...splitTargets.projects
      .filter(p => p._id !== (splitExp?.project?._id || splitExp?.project))
      .filter(p => `${p.poNumber || ''} ${p.title || ''} ${p.customer?.name || ''}`.toLowerCase().includes(splitDestQ.trim().toLowerCase()))
      .slice(0, 6)
      .map(p => ({ kind: 'project', id: p._id, label: `PO ${p.poNumber || '—'} — ${p.title || p.customer?.name || ''}` })),
    ...splitTargets.cats
      .filter(c => `${c.code || ''} ${c.name || ''} overhead`.toLowerCase().includes(splitDestQ.trim().toLowerCase()))
      .slice(0, 4)
      .map(c => ({ kind: 'overhead', id: c._id, label: `Overhead — ${c.name || c.code}` })),
  ] : [];
  const doSplit = async () => {
    if (splitBusy || !splitExp || !splitDest) return;
    setSplitBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/expenses/${splitExp._id}/split`, {
        itemIndexes: splitItems.length ? splitPicks : undefined,
        manualAmount: splitItems.length ? undefined : Number(splitManual.amount),
        manualLabel: splitManual.label,
        destProject: splitDest.kind === 'project' ? splitDest.id : undefined,
        destCostCategory: splitDest.kind === 'overhead' ? splitDest.id : undefined,
      }, { headers: { Authorization: token } });
      setSplitExp(null);
      await onRefresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not split the expense.');
    } finally {
      setSplitBusy(false);
    }
  };

  const handleRightClick = (e, expenseId) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, expenseId });
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/expenses/${expenseId}`, { headers: { Authorization: token } });
      await onRefresh();
      setContextMenu(null);
    } catch (err) {
      alert("Failed to delete expense.");
    }
  };

  // Date-only values ("2026-09-02" / ISO midnight) must be read as a CALENDAR
  // date, not a UTC instant — new Date() shifts them a day early in Pacific.
  const fmtExpDate = (v) => {
    if (!v) return '—';
    const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString();
    return new Date(v).toLocaleDateString();
  };

  // ---- Swipe-to-delete (mobile cards): swipe left to reveal Delete ----
  // Reveal-then-tap only (no full-swipe shortcut). One card open at a time.
  const [swipedKey, setSwipedKey] = useState(null);
  const [swipeState, setSwipeState] = useState({ key: null, dx: 0, active: false });
  const swipeAteClickAt = useRef(0); // eats the trailing click after a swipe
  const trackSwipe = (key, e) => {
    if (e.pointerType === 'mouse') return; // touch/pen only
    const sx = e.clientX, sy = e.clientY;
    let decided = null, lastDx = 0;
    const off = () => {
      window.removeEventListener('pointermove', mv);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    const mv = (ev) => {
      const dx = ev.clientX - sx, dy = ev.clientY - sy;
      if (!decided) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        decided = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
        if (decided === 'v') { off(); return; } // scrolling — leave it alone
      }
      lastDx = Math.max(-100, Math.min(20, dx));
      setSwipeState({ key, dx: Math.min(0, lastDx), active: true });
    };
    const up = () => {
      off();
      setSwipeState({ key: null, dx: 0, active: false });
      if (decided === 'h') {
        swipeAteClickAt.current = Date.now();
        if (lastDx < -45) setSwipedKey(key);           // opened
        else setSwipedKey(k => (k === key ? null : k)); // closed / not enough
      }
    };
    window.addEventListener('pointermove', mv);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };
  // Any tap elsewhere closes an open swipe.
  useEffect(() => {
    if (!swipedKey) return;
    const close = () => setSwipedKey(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [swipedKey]);

  const SwipeWrap = (key, onDelete, children, rounded = '') => {
    const open = swipedKey === key;
    const liveDx = swipeState.key === key ? swipeState.dx : 0;
    const dx = open ? Math.min(-72, liveDx || -72) : Math.min(0, liveDx);
    return (
      <div className={`relative overflow-hidden ${rounded}`}>
        {(open || dx < 0) && (
          <button
            onClick={(e) => { e.stopPropagation(); setSwipedKey(null); onDelete(e); }}
            className="absolute inset-y-0 right-0 w-20 bg-red-600 active:bg-red-500 text-white text-xs font-black uppercase flex items-center justify-center"
          >
            Delete
          </button>
        )}
        <div
          style={{ transform: `translateX(${dx}px)`, transition: swipeState.key === key && swipeState.active ? 'none' : 'transform 0.22s ease' }}
          className="relative"
        >
          {children}
        </div>
      </div>
    );
  };

  return (
<div>
      {/* --- BEAUTIFUL TOAST NOTIFICATIONS --- */}
      
      {/* 1. SUCCESS TOAST */}
      {showSuccessToast && (
        <div className="fixed top-6 max-md:top-16 left-1/2 -translate-x-1/2 w-auto max-w-[calc(100vw-2rem)] bg-emerald-500 text-white px-5 py-3 rounded-2xl shadow-[0_8px_30px_rgba(16,185,129,0.35)] flex items-center gap-3 z-[9999] font-bold border border-emerald-400">
          <div className="bg-white text-emerald-600 rounded-full p-1 shrink-0">
            <Check size={16} strokeWidth={3} />
          </div>
          Batch Saved Successfully!
        </div>
      )}

      {/* 2. ERROR TOAST — stationary, width-capped, wraps long messages */}
      {saveError && (
        <div className="fixed top-6 max-md:top-16 left-1/2 -translate-x-1/2 w-[calc(100vw-2rem)] max-w-md bg-red-600 text-white px-4 py-3 rounded-2xl shadow-[0_8px_30px_rgba(220,38,38,0.45)] flex items-start gap-3 z-[9999] border border-red-400">
          <div className="bg-white text-red-600 rounded-full p-1 shrink-0 mt-0.5">
            <X size={14} strokeWidth={3} />
          </div>
          <p className="text-sm font-medium leading-snug min-w-0 break-words flex-1">{saveError}</p>
          <button
            onClick={() => setSaveError(null)}
            className="shrink-0 hover:bg-red-700 p-1.5 rounded-full transition-colors flex items-center justify-center"
          >
            <X size={16} className="text-white" />
          </button>
        </div>
      )}
      {/* --- END TOAST NOTIFICATIONS --- */}

      {/* Top Action Bar — stacks into full-width buttons on phones */}
<div className="flex justify-between items-center mb-6 max-md:flex-col max-md:items-stretch max-md:gap-3">
  <h2 className="text-2xl max-md:text-xl font-bold text-white">Project Expenses</h2>

  <div className="flex gap-4 max-md:flex-col max-md:gap-2.5">
    {/* 1. YOUR MANUAL ENTRY BUTTON */}
    <button 
      onClick={() => setShowAddExpenseModal(true)}
      className="flex items-center gap-2 max-md:justify-center bg-zinc-800 hover:bg-zinc-700 px-5 py-3 rounded-xl text-white font-medium transition-all"
    >
      <Plus size={20} /> Add Expense
    </button>

    {/* 2. YOUR DYNAMIC UPLOAD/SCAN BUTTON */}
    {(isUploading || pendingReceipts.some(p => p.status === 'Processing')) ? (
      <button 
        onClick={() => setShowAddReceiptModal(true)} 
        className="flex items-center gap-2 max-md:justify-center bg-indigo-600/20 border border-indigo-500/30 px-5 py-3 rounded-xl text-indigo-400 font-medium animate-pulse"
      >
        <div className="w-4 h-4 border-2 border-indigo-400/30 border-t-indigo-400 rounded-full animate-spin" />
        Scanning in Background...
      </button>
    ) : pendingReceipts.some(p => p.status === 'Ready') ? (
      <button 
        onClick={() => {
          const readyBatch = pendingReceipts.find(p => p.status === 'Ready');
          setBatchData(readyBatch.extractedData);
          setSelectedReceipts(readyBatch.extractedData.map(r => r.id));
          setCurrentPendingId(readyBatch._id);
          setShowExtractionReview(true);
          setIsUploading(false); // Reset the local upload state once we hit review
        }}
        className="flex items-center gap-2 max-md:justify-center bg-blue-600 hover:bg-blue-500 px-5 py-3 rounded-xl text-white font-bold transition-all shadow-lg animate-pulse"
      >
        <Check size={20} /> Review Pending ({pendingReceipts.filter(p => p.status === 'Ready').reduce((total, batch) => total + (batch.extractedData ? batch.extractedData.length : 0), 0)})
      </button>
    ) : (
      <button 
        onClick={() => setShowAddReceiptModal(true)}
        className="flex items-center gap-2 max-md:justify-center bg-indigo-600 hover:bg-indigo-500 px-5 py-3 rounded-xl text-white font-medium transition-all"
      >
        <Upload size={20} /> Add Receipt
      </button>
    )}
  </div>
</div>

{/* Expenses Table — also a hidden drag-drop zone (easter egg) */}
<div
  className="expenses-table-container relative"
  onDragOver={(e) => {
    if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); if (!tableDragging) setTableDragging(true); }
  }}
  onDragLeave={(e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setTableDragging(false);
  }}
  onDrop={(e) => {
    e.preventDefault();
    setTableDragging(false);
    const files = Array.from(e.dataTransfer.files || []).filter(
      f => f.type.startsWith('image/') || f.type === 'application/pdf'
    );
    if (files.length) handleUploadReceipts(files);
  }}
>
  {/* Drop affordance — appears ONLY while a file is being dragged over. No text. */}
  {tableDragging && (
    <div className="absolute inset-0 z-20 rounded-2xl border-2 border-dashed border-indigo-500 bg-indigo-500/10 pointer-events-none" />
  )}
  {/* ===== MOBILE EXPENSE CARDS (phones only — desktop table below untouched) ===== */}
  <div className="md:hidden space-y-2.5 pb-4">
    {expenses.length === 0 ? (
      <p className="text-center text-zinc-500 py-8">No expenses yet. Upload receipts or add manually.</p>
    ) : (
      expenses.map((exp) => {
        const val = Number(exp.totalAmount || exp.amount || 0);
        const isCredit = exp.isReturn || val < 0;
        return (
          <React.Fragment key={exp._id}>
          {SwipeWrap(`exp:${exp._id}`, () => handleDeleteExpense(exp._id),
          <div
            onPointerDown={(e) => trackSwipe(`exp:${exp._id}`, e)}
            onClick={() => { if (Date.now() - swipeAteClickAt.current < 350) return; exp.receiptImagePath && viewSavedReceipt(exp); }}
            onContextMenu={(e) => handleRightClick(e, exp._id)}
            style={{ touchAction: 'pan-y' }}
            className={`bg-gray-900 border rounded-xl p-3.5 transition-colors ${
              exp.needsReview ? 'border-amber-500/60' : 'border-gray-800/60'
            } ${exp.receiptImagePath ? 'active:bg-gray-800/70' : ''}`}
          >
            <div className="flex items-center justify-between gap-2">
              <p className="text-zinc-100 font-semibold text-sm min-w-0 break-words">
                {exp.needsReview ? '⚠️ ' : ''}{exp.isReturn ? '↩︎ ' : ''}{exp.store || '—'}
              </p>
              <p className={`font-bold text-sm shrink-0 ${isCredit ? 'text-emerald-400' : 'text-red-400'}`}>
                {isCredit ? '+' : '-'}${Math.abs(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
            </div>
            <p className="text-zinc-500 text-xs mt-1">
              {fmtExpDate(exp.date)}
              {exp.category ? ` · ${exp.category}` : ''}
              {exp.receiptNumber ? ` · #${exp.receiptNumber}` : ''}
            </p>
            {(exp.splitCount || 1) <= 1 && (
              <p
                onClick={(e) => handleNoteTap(exp, e)}
                className={`text-xs mt-0.5 break-words ${exp.notes ? 'text-zinc-600' : 'text-zinc-700 italic py-1'}`}
              >
                {NoteCell(exp, 'Double-tap to add a note')}
              </p>
            )}
            {exp.receiptImagePath && <p className="text-zinc-600 text-[11px] mt-1">Tap to view receipt</p>}
          </div>
          , 'rounded-xl')}
          </React.Fragment>
        );
      })
    )}
  </div>

  <div className="max-md:hidden bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-800/60">
      <thead className="bg-gray-950/30">
        <tr>
          <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Date</th>
          <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Store</th>
          <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Receipt #</th>
          <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Category</th>
          <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Notes</th>
          <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase text-right">Amount</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-800">
  {expenses.length > 0 ? (
    expenses.map((exp, index) => {
      const isLinked = !!exp.linkId;
      const linkedGroup = expenses.filter(e => e.linkId && e.linkId === exp.linkId);
      const isPrimary = isLinked
        ? exp.totalAmount === Math.max(...linkedGroup.map(g => g.totalAmount || 0))
        : true;
      const linkedPartner = isLinked ? linkedGroup.find(e => e._id !== exp._id) : null;

      return (
        <tr
  key={exp._id}
  id={`exp-row-${exp._id}`}
  onClick={(e) => handleRowClick(e, index, exp._id)}
  onContextMenu={(e) => handleRightClick(e, exp._id)}
  className={`
    border-b border-zinc-800/50 transition-all cursor-pointer select-none
    ${selectedRows.includes(exp._id)
      ? 'bg-blue-500/30 ring-1 ring-blue-500'           // ← Stronger highlight when selected
      : 'hover:bg-zinc-800/40'
    }
    ${isLinked ? 'bg-indigo-500/5' : ''}
    ${highlightId === exp._id ? 'bg-indigo-500/20 ring-2 ring-inset ring-indigo-400' : ''}
    ${highlightPurchaseKey && !exp.isReturn && (exp.normalizedReceiptNumber === highlightPurchaseKey || exp.receiptNumber === highlightPurchaseKey) ? 'bg-emerald-500/15 ring-2 ring-inset ring-emerald-400' : ''}
  `}
>
          <td className="px-6 py-4 whitespace-nowrap text-sm">
            {fmtExpDate(exp.date)}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm">
            <div className="flex items-center gap-2">
              {isLinked && (
                <span className="text-indigo-400" title={isPrimary ? "Primary (counts in total)" : "Secondary (linked, not counted)"}>
                  {isPrimary ? "🔗" : "📎"}
                </span>
              )}
              {exp.needsReview && (
                <span className="text-amber-400" title={exp.reviewReason || 'Needs review'}>⚠️</span>
              )}
              {!exp.needsReview && exp.mathNote && (
                <span className="text-zinc-500 text-xs" title={exp.mathNote}>ⓘ</span>
              )}
              <div className="flex flex-col">
                <span>{exp.store}{exp.storeLocation ? <span className="text-zinc-500"> · {exp.storeLocation}</span> : null}</span>
                {linkedPartner && (
                  <span className="text-[10px] text-indigo-400/80" title={`Linked to ${linkedPartner.receiptNumber}`}>
                    linked to #{linkedPartner.receiptNumber}
                  </span>
                )}
              </div>
            </div>
          </td>
          <td className="px-6 py-4 text-sm font-mono">
            <div className="flex flex-col">
              {exp.receiptImagePath ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); viewSavedReceipt(exp); }}
                  className="text-left text-indigo-300 hover:text-indigo-200 underline underline-offset-2"
                  title="Open receipt PDF"
                >
                  {exp.receiptNumber || '—'}
                </button>
              ) : (<span>{exp.receiptNumber || '—'}</span>)}
              {/* Multi-document packet (Rexel Acknowledgement + Ship Ticket): the
                  receipt-number link opens all linked documents merged into one
                  PDF at view time, so no per-document chips are needed. */}
              {Array.isArray(exp.attachments) && exp.attachments.length > 1 && (
                <span className="mt-1 w-fit text-[10px] font-semibold text-sky-300/80">
                  {exp.attachments.length} documents · opens as one PDF
                </span>
              )}
              {/* Split-return badge: which slice of a multi-reference return this
                  is. Click to flash the other slices of the same slip. */}
              {exp.splitCount > 1 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const key = exp.normalizedOriginalReceiptNumber || exp.originalReceiptNumber;
                    if (!key) return;
                    setHighlightPurchaseKey(key);
                    setTimeout(() => {
                      const target = (expenses || []).find(
                        x => !x.isReturn && (x.normalizedReceiptNumber === key || x.receiptNumber === key)
                      );
                      if (target) {
                        const el = document.getElementById(`exp-row-${target._id}`);
                        if (el) {
                          const rect = el.getBoundingClientRect();
                          const inView = rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
                          if (!inView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }
                    }, 30);
                    setTimeout(() => setHighlightPurchaseKey(null), 300);
                  }}
                  title="Click to highlight the original purchase this refunds"
                  className="mt-0.5 w-fit text-[10px] font-semibold text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 px-1.5 py-0.5 rounded"
                >
                  {(exp.splitIndex ?? 0) + 1} of {exp.splitCount} · part of return #{exp.receiptNumber}
                </button>
              )}
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm">{exp.category}</td>
          <td
            onDoubleClick={(e) => { if (exp.splitCount > 1) return; e.stopPropagation(); startNoteEdit(exp); }}
            title={exp.splitCount > 1 ? undefined : 'Double-click to edit the note'}
            className="px-6 py-4 text-sm"
          >
            {exp.splitCount > 1 ? null : NoteCell(exp)}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold">
            <div className="flex flex-col items-end">
              {(() => {
                const val = Number(exp.totalAmount || exp.amount || 0);
                const isCredit = exp.isReturn || val < 0; // returns show as positive credits
                return (
                  <span className={isCredit ? 'text-emerald-400' : 'text-red-400'}>
                    {isCredit ? '+' : '-'}${Math.abs(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                );
              })()}
              {isLinked && !isPrimary && (
                <span className="text-[9px] text-orange-400 font-medium">Secondary (skipped in total)</span>
              )}
            </div>
          </td>
        </tr>
      );
    })
  ) : (
    <tr>
      <td colSpan="6" className="px-6 py-8 text-center text-zinc-500">
        No expenses yet. Upload receipts or add manually.
      </td>
    </tr>
  )}
</tbody>
    </table>
  </div>
</div>
</div>

      {/* Context Menu */}
      {contextMenu && (
        <>
        {/* click-away overlay so the menu dismisses like every other menu */}
        <div className="fixed inset-0 z-40" onClick={() => setContextMenu(null)} onContextMenu={(e) => { e.preventDefault(); setContextMenu(null); }} />
        <div className="fixed bg-gray-900 border border-gray-700 rounded-lg shadow-2xl py-2 z-50 text-sm" style={{ top: `${Math.min(contextMenu.y, (window.innerHeight || 600) - 170)}px`, left: `${Math.min(contextMenu.x, (window.innerWidth || 800) - 200)}px` }}>
          <button className="w-full px-6 py-2 text-left hover:bg-gray-800" onClick={() => alert("Edit coming soon")}>Edit Expense</button>
          <button className="w-full px-6 py-2 text-left text-zinc-200 hover:bg-gray-800" onClick={() => openEditItems(contextMenu.expenseId)}>Edit Items…</button>
          <button className="w-full px-6 py-2 text-left text-zinc-200 hover:bg-gray-800" onClick={() => openSplit(contextMenu.expenseId)}>Split Expense…</button>
          <button className="w-full px-6 py-2 text-left text-red-400 hover:bg-gray-800 hover:text-red-300" onClick={() => handleDeleteExpense(contextMenu.expenseId)}>Delete Expense</button>
        </div>
        </>
      )}

      {/* ====================== EDIT ITEMS MODAL ====================== */}
      {itemsExp && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[110] p-4 backdrop-blur-sm">
          <div className="bg-gray-900 rounded-2xl p-7 w-full max-w-2xl border border-gray-700 shadow-2xl max-h-[90dvh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-1 text-white">Edit Items</h2>
            <p className="text-zinc-500 text-sm mb-5">
              {itemsExp.store || 'Receipt'} · booked subtotal ${Number(itemsExp.amount || 0).toFixed(2)} — fix or add lines the scan missed. Booked totals never change here.
            </p>
            <div className="hidden md:grid grid-cols-[1fr_110px_60px_90px_90px_28px] gap-2 text-[11px] uppercase text-zinc-500 font-bold mb-1 px-1">
              <span>Item</span><span>SKU</span><span>Qty</span><span>Unit $</span><span>Total $</span><span />
            </div>
            <div className="space-y-2 mb-4">
              {itemRows.map((r, i) => (
                <div key={i} className="grid grid-cols-[1fr_110px_60px_90px_90px_28px] max-md:grid-cols-2 gap-2 items-center">
                  <input placeholder="Item name" value={r.name} onChange={(e) => setItemRow(i, 'name', e.target.value)}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-indigo-500 outline-none max-md:col-span-2" />
                  <input placeholder="SKU" value={r.sku} onChange={(e) => setItemRow(i, 'sku', e.target.value)}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-indigo-500 outline-none" />
                  <input placeholder="Qty" inputMode="decimal" value={r.quantity} onChange={(e) => setItemRow(i, 'quantity', e.target.value.replace(/[^0-9.]/g, ''))}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-indigo-500 outline-none" />
                  <input placeholder="Unit" inputMode="decimal" value={r.unitPrice} onChange={(e) => setItemRow(i, 'unitPrice', e.target.value.replace(/[^0-9.]/g, ''))}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-indigo-500 outline-none" />
                  <input placeholder="Total" inputMode="decimal" value={r.total} onChange={(e) => setItemRow(i, 'total', e.target.value.replace(/[^0-9.]/g, ''))}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-indigo-500 outline-none" />
                  <button onClick={() => setItemRows((rows) => rows.filter((_, j) => j !== i))} className="text-zinc-600 hover:text-red-400 text-lg">×</button>
                </div>
              ))}
            </div>
            <button onClick={() => setItemRows((rows) => [...rows, { name: '', sku: '', quantity: 1, unitPrice: '', total: '' }])}
              className="text-xs text-indigo-400 hover:text-indigo-300 font-semibold mb-4">+ Add item</button>
            <div className={`rounded-xl px-4 py-3 mb-6 text-sm border ${Math.abs(itemRowsSum - Number(itemsExp.amount || 0)) <= 0.05 ? 'bg-emerald-500/10 border-emerald-600/40 text-emerald-300' : 'bg-amber-500/10 border-amber-600/40 text-amber-300'}`}>
              Items sum to <span className="font-bold">${itemRowsSum.toFixed(2)}</span> · booked subtotal ${Number(itemsExp.amount || 0).toFixed(2)}
              {Math.abs(itemRowsSum - Number(itemsExp.amount || 0)) <= 0.05 ? ' — reconciles ✓' : ' — off by $' + Math.abs(itemRowsSum - Number(itemsExp.amount || 0)).toFixed(2) + ' (saving is still allowed; a note is kept)'}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setItemsExp(null)} className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-zinc-300 rounded-xl font-medium">Cancel</button>
              <button onClick={saveItems} disabled={itemsBusy || !itemRows.some((r) => r.name.trim())}
                className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded-xl font-bold">
                {itemsBusy ? 'Saving…' : 'Save Items'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====================== SPLIT EXPENSE MODAL ====================== */}
      {splitExp && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[110] p-4 backdrop-blur-sm">
          <div className="bg-gray-900 rounded-2xl p-7 w-full max-w-lg border border-gray-700 shadow-2xl max-h-[90dvh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-1 text-white">Split Expense</h2>
            <p className="text-zinc-500 text-sm mb-5">
              {splitExp.store || 'Receipt'} · ${Number(splitExp.amount || 0).toFixed(2)} + ${Number(splitExp.taxAmount || 0).toFixed(2)} tax — pick what moves; tax prorates automatically. Both records keep the same receipt file.
            </p>

            {/* prior surgery on this receipt — shown before more is done */}
            {(splitHist?.children?.length > 0 || splitHist?.parent) && (
              <div className="bg-gray-950/60 border border-amber-700/40 rounded-xl px-4 py-3 mb-4 space-y-1.5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-300/90">Previous splits on this receipt</p>
                {splitHist.parent && (
                  <p className="text-xs text-zinc-400">
                    This expense was itself split out of a {splitHist.parent.store || 'receipt'} expense on {splitDestOf(splitHist.parent)} (${Number(splitHist.parent.amount || 0).toFixed(2)} + ${Number(splitHist.parent.taxAmount || 0).toFixed(2)} tax remained there).
                  </p>
                )}
                {(splitHist.children || []).map((c, i) => {
                  const srcPo = (splitTargets.projects || []).find((p) => p._id === projectId)?.poNumber;
                  const tot = c.totalAmount != null ? Number(c.totalAmount) : Number(c.amount || 0) + Number(c.taxAmount || 0);
                  return (
                    <p key={i} className="text-xs text-zinc-400">
                      → from <span className="font-bold text-zinc-200">PO {srcPo || '—'}</span> to <span className="font-bold text-indigo-300">{splitDestOf(c)}</span>: ${Number(c.amount || 0).toFixed(2)} + ${Number(c.taxAmount || 0).toFixed(2)} tax = <span className="font-bold text-emerald-300">${tot.toFixed(2)}</span>
                      {c.createdAt ? <span className="text-zinc-600"> · {new Date(c.createdAt).toLocaleDateString()}</span> : null}
                      {c.notes ? <span className="text-zinc-600"> — {c.notes}</span> : null}
                    </p>
                  );
                })}
              </div>
            )}

            {splitItems.length ? (
              <div className="space-y-1.5 mb-5">
                {splitItems.map((it, i) => (
                  <label key={i} className="flex items-center gap-3 bg-gray-950/60 border border-gray-800/70 rounded-xl px-3.5 py-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={splitPicks.includes(i)}
                      onChange={() => setSplitPicks(p => p.includes(i) ? p.filter(x => x !== i) : [...p, i])}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-zinc-200 flex-1 min-w-0 break-words">{it.name || 'Item'}{Number(it.quantity) > 1 ? ` ×${it.quantity}` : ''}</span>
                    <span className="text-sm font-semibold text-green-400 shrink-0">${Number(it.total || 0).toFixed(2)}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="space-y-3 mb-5">
                <p className="text-xs text-amber-300/90">No line items were read from this receipt — enter the pre-tax amount to carve out.</p>
                <input placeholder="Pre-tax amount, e.g. 15.97" inputMode="decimal" value={splitManual.amount}
                  onChange={(e) => setSplitManual(m => ({ ...m, amount: e.target.value.replace(/[^0-9.]/g, '') }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-indigo-500 outline-none" />
                <input placeholder="What is it? e.g. Pro utility knife" value={splitManual.label}
                  onChange={(e) => setSplitManual(m => ({ ...m, label: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-indigo-500 outline-none" />
              </div>
            )}

            {/* destination: project PO or overhead category */}
            <div className="relative mb-5">
              <label className="block text-zinc-400 text-sm mb-2">Move to</label>
              {splitDest ? (
                <div className="flex items-center justify-between gap-2 bg-indigo-500/10 border border-indigo-500/40 rounded-xl px-4 py-3">
                  <span className="text-sm text-indigo-200 truncate">{splitDest.label}</span>
                  <button onClick={() => { setSplitDest(null); setSplitDestQ(''); }} className="text-xs font-semibold text-indigo-300 shrink-0">Change</button>
                </div>
              ) : (
                <>
                  <input autoFocus={!splitItems.length ? false : true} placeholder="Type a PO number, project, or overhead category…"
                    value={splitDestQ} onChange={(e) => setSplitDestQ(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-indigo-500 outline-none" />
                  {splitMatches.length > 0 && (
                    <div className="absolute z-20 left-0 right-0 mt-1 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl overflow-hidden">
                      {splitMatches.map((m, i) => (
                        <button key={i} onClick={() => setSplitDest(m)} className="w-full flex items-center gap-2 px-3.5 py-2.5 hover:bg-gray-800 text-left">
                          <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0 ${m.kind === 'overhead' ? 'bg-amber-500/15 text-amber-300' : 'bg-sky-500/15 text-sky-300'}`}>{m.kind === 'overhead' ? 'Overhead' : 'Project'}</span>
                          <span className="text-sm text-zinc-200 truncate">{m.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* live math */}
            {splitPreTax > 0 && (
              <div className="bg-gray-950/60 border border-gray-800/70 rounded-xl px-4 py-3 mb-6 text-sm text-zinc-300">
                Moving <span className="font-bold text-white">${splitPreTax.toFixed(2)}</span> + <span className="font-bold text-white">${splitTaxShare.toFixed(2)}</span> prorated tax = <span className="font-bold text-emerald-300">${(splitPreTax + splitTaxShare).toFixed(2)} total</span>
                <span className="text-zinc-500"> — this expense keeps ${(Number(splitExp.amount || 0) - splitPreTax).toFixed(2)} + ${(Number(splitExp.taxAmount || 0) - splitTaxShare).toFixed(2)} tax = ${(Number(splitExp.amount || 0) - splitPreTax + Number(splitExp.taxAmount || 0) - splitTaxShare).toFixed(2)}</span>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => setSplitExp(null)} className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-zinc-300 rounded-xl font-medium">Cancel</button>
              <button
                onClick={doSplit}
                disabled={splitBusy || !splitDest || !(splitPreTax > 0) || splitPreTax >= Number(splitExp.amount || 0) || (!splitItems.length && !splitManual.label.trim())}
                className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded-xl font-bold"
              >
                {splitBusy ? 'Splitting…' : 'Split'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====================== MANUAL ADD EXPENSE MODAL ====================== */}
      {showAddExpenseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[100]">
          <div className="bg-gray-900 rounded-2xl p-8 w-full max-w-md border border-gray-700">
            {!isItemMode ? (
              <>
                <h2 className="text-2xl font-bold mb-6">Add New Expense</h2>
                <div className="space-y-6">
                  <div>
                    <label className="block text-zinc-400 text-sm mb-2">Date</label>
                    <input type="date" value={newExpense.date} onChange={(e) => setNewExpense({...newExpense, date: e.target.value})} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white" />
                  </div>

                  {/* Category first — it decides which fields show below. */}
                  <div>
                    <label className="block text-zinc-400 text-sm mb-2">Category</label>
                    <select value={newExpense.category} onChange={(e) => setNewExpense({...newExpense, category: e.target.value})} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white">
                      <option value="Materials">Materials</option>
                      <option value="Tools">Tools</option>
                      <option value="Equipment">Equipment</option>
                      <option value="Equipment Rental">Equipment Rental</option>
                      <option value="Labor">Labor</option>
                      <option value="Subcontractor">Subcontractor</option>
                      <option value="Permits">Permits</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {isLabor ? (
                    /* ---- LABOR: trade, payee, amount, payment method ---- */
                    <>
                      <div>
                        <label className="block text-zinc-400 text-sm mb-2">Type of labor</label>
                        <input
                          list="laborTypeOptions"
                          value={newExpense.laborType}
                          onChange={(e) => setNewExpense({...newExpense, laborType: e.target.value})}
                          className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white"
                          placeholder="e.g. Lath & Stucco"
                        />
                        <datalist id="laborTypeOptions">
                          {LABOR_TYPES.filter(t => t !== 'Other').map(t => <option key={t} value={t} />)}
                        </datalist>
                      </div>

                      <div>
                        <label className="block text-zinc-400 text-sm mb-2">Paid to <span className="text-zinc-600">(optional)</span></label>
                        <input type="text" value={newExpense.payee} onChange={(e) => setNewExpense({...newExpense, payee: e.target.value})} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white" placeholder="Worker or company name" />
                      </div>

                      <div>
                        <label className="block text-zinc-400 text-sm mb-2">Amount paid</label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                          <input type="text" value={newExpense.laborAmount} onChange={(e) => setNewExpense({...newExpense, laborAmount: formatPrice(e.target.value)})} className="w-full bg-gray-800 border border-gray-700 rounded-xl pl-8 pr-4 py-3 text-white" placeholder="200.00" />
                        </div>
                      </div>

                      <div>
                        <label className="block text-zinc-400 text-sm mb-2">Payment method</label>
                        <select value={newExpense.paymentMethod} onChange={(e) => setNewExpense({...newExpense, paymentMethod: e.target.value})} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white">
                          {LABOR_PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </div>

                      <div>
                        <label className="block text-zinc-400 text-sm mb-2">Notes <span className="text-zinc-600">(optional)</span></label>
                        <input type="text" value={newExpense.notes} onChange={(e) => setNewExpense({...newExpense, notes: e.target.value})} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white" placeholder="What was the work?" />
                      </div>

                      <div className="pt-4 border-t border-gray-700 flex justify-between text-lg font-semibold">
                        <span>Total</span>
                        <span className="text-emerald-400">${Number(parseFloat(String(newExpense.laborAmount).replace(/,/g, '')) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                      </div>
                    </>
                  ) : (
                    /* ---- STANDARD: store, receipt, tax ---- */
                    <>
                      <div>
                        <label className="block text-zinc-400 text-sm mb-2">Store</label>
                        <select value={newExpense.store} onChange={(e) => setNewExpense({...newExpense, store: e.target.value})} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white">
                          <option value="Home Depot">Home Depot</option>
                          <option value="Lowes">Lowe’s</option>
                          <option value="Platt">Platt Electric</option>
                          <option value="Rexel">Rexel</option>
                          <option value="CED">CED</option>
                          <option value="ACE Hardware">ACE Hardware</option>
                          <option value="Permit">Permit</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-zinc-400 text-sm mb-2">Receipt Number <span className="text-zinc-500 font-normal">(optional)</span></label>
                        <input type="text" value={newExpense.receiptNumber} onChange={(e) => setNewExpense({...newExpense, receiptNumber: e.target.value})} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white" placeholder="HD-7843921" />
                      </div>

                      <div>
                        <label className="block text-zinc-400 text-sm mb-2">Sales Tax Rate (%)</label>
                        <div className="relative">
                          <input type="text" value={newExpense.taxRate} onChange={(e) => setNewExpense({...newExpense, taxRate: formatTaxRate(e.target.value)})} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white pr-8" placeholder="7.75" />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 font-medium">%</span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-zinc-400 text-sm mb-2">Total charged <span className="text-zinc-500 font-normal">(tax included)</span></label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 font-medium">$</span>
                          <input type="text" inputMode="decimal" value={newExpense.manualTotal} onChange={(e) => setNewExpense({ ...newExpense, manualTotal: e.target.value })} disabled={itemsSubtotal > 0} className="w-full bg-gray-800 border border-gray-700 rounded-xl pl-8 pr-4 py-3 text-white disabled:opacity-40" placeholder="215.00" />
                        </div>
                        <p className="text-xs text-zinc-500 mt-1.5">{itemsSubtotal > 0 ? 'Using your itemized entries — clear them to type a total directly.' : 'No receipt? Enter the total charged; tax is split out using the rate above.'}</p>
                      </div>

                      <div className="pt-4 border-t border-gray-700">
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-400">Subtotal (pre-tax)</span>
                          <span className="font-medium text-zinc-200">${Number(subtotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between text-sm mt-2">
                          <span className="text-zinc-400">Tax Amount</span>
                          <span className="font-medium text-red-400">${taxAmount}</span>
                        </div>
                        <div className="flex justify-between text-lg font-semibold mt-4 pt-3 border-t border-gray-700">
                          <span>Total Amount</span>
                          <span className="text-emerald-400">${Number(totalAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-zinc-400 text-sm mb-2">Notes</label>
                        <input type="text" value={newExpense.notes} onChange={(e) => setNewExpense({...newExpense, notes: e.target.value})} className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white" placeholder="What was this expense for?" />
                      </div>
                    </>
                  )}
                </div>

                <div className="flex gap-4 mt-10">
                  <button onClick={() => setShowAddExpenseModal(false)} className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-medium">Cancel</button>
                  {!isLabor && (
                    <button onClick={() => setIsItemMode(true)} className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-medium">Enter Items</button>
                  )}
                  <button onClick={handleAddExpense} className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-medium">{isLabor ? 'Save Labor' : 'Save Expense'}</button>
                </div>
              </>
            ) : (
              // Item Entry Mode
              <>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold">Enter Receipt Items</h2>
                  <button onClick={handleDoneWithItems} className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-medium">Done</button>
                </div>

                <div className="bg-gray-950 rounded-xl p-6 border border-gray-700">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 text-zinc-400">Item Name</th>
                        <th className="text-right py-3 text-zinc-400">Price</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => (
                        <tr key={item.id} className="border-b border-gray-800 last:border-none">
                          <td className="py-3">
                            <input
                              ref={el => nameRefs.current[index] = el}
                              type="text"
                              value={item.name}
                              onChange={(e) => updateItem(item.id, 'name', e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (priceRefs.current[index]) priceRefs.current[index].focus(); } }}
                              className="w-full bg-transparent border-none focus:outline-none text-white"
                              placeholder="Item name"
                            />
                          </td>
                          <td className="py-3 text-right">
                            <input
                              ref={el => priceRefs.current[index] = el}
                              type="text"
                              value={item.price}
                              onChange={(e) => updateItem(item.id, 'price', e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNewItemRow(); } }}
                              className="w-36 bg-transparent border-none focus:outline-none text-right text-white"
                              placeholder="0.00"
                            />
                          </td>
                          <td className="py-3">
                            <button onClick={() => deleteItem(item.id)} className="text-red-400 hover:text-red-300">
                              <Trash2 size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 flex justify-between text-sm">
                  <button onClick={addNewItemRow} className="text-indigo-400 hover:text-indigo-300 flex items-center gap-1">+ Add another item</button>
                  <div className="text-right">
                    <div>Subtotal: <span className="font-medium">${subtotal.toFixed(2)}</span></div>
                    <div>Tax ({taxRateNum}%): <span className="font-medium text-red-400">${taxAmount}</span></div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

     {/* ====================== ADD RECEIPT MODAL (BATCH COMPATIBLE) ====================== */}
{showAddReceiptModal && (
  <div 
    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
    onClick={() => setShowAddReceiptModal(false)} // Clicking background HIDES the modal
  >
    <div 
      className="bg-zinc-900 border border-zinc-800 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden"
      onClick={(e) => e.stopPropagation()} // Prevents closing when clicking inside card
    >
      <div className="p-6 border-b border-zinc-800 flex justify-between items-center">
        <h3 className="text-xl font-bold text-white">Add Project Receipts</h3>
        <button onClick={() => setShowAddReceiptModal(false)} className="text-zinc-500 hover:text-white">
          <X size={24} />
        </button>
      </div>

      <div className="p-8">
        {/* PROGRESS BAR VIEW: Only shows if we are currently uploading or processing */}
        {isUploading || pendingReceipts.some(p => p.status === 'Processing') ? (
          <div className="py-12 text-center">
            <div className="w-full bg-zinc-800 h-3 rounded-full overflow-hidden mb-6 relative">
              <div 
                className="bg-indigo-500 h-full rounded-full shadow-[0_0_15px_rgba(79,70,229,0.6)]" 
                style={{ 
                  width: '30%', 
                  animation: 'scanningSlide 1.5s infinite alternate ease-in-out' 
                }} 
              />
              <style>{`
                @keyframes scanningSlide {
                  0% { transform: translateX(0%); }
                  100% { transform: translateX(233%); }
                }
              `}</style>
            </div>
            <h4 className="text-white font-medium text-lg mb-2">Processing with Grok...</h4>
            <p className="text-zinc-400">The files are at the server. You can close this window and keep working; the "Add Receipt" button will change to "Review" when finished.</p>
          </div>
        ) : (
          /* REGULAR UPLOAD VIEW */
          /* REGULAR UPLOAD VIEW */
          <div 
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              enqueueReceiptFiles(e.dataTransfer.files);
            }}
            className="border-2 border-dashed border-zinc-800 rounded-xl p-12 text-center hover:border-indigo-500/50 transition-colors"
          >
            <Upload size={48} className="mx-auto mb-4 text-zinc-600" />
            <h4 className="text-lg font-medium text-white mb-2">Drag & Drop receipts here</h4>
            <p className="text-zinc-500 mb-6">or click to browse your files (PDF, JPG, PNG)</p>
            
            {/* The hidden file input that makes browsing work */}
            <input
              type="file"
              multiple
              onChange={(e) => { enqueueReceiptFiles(e.target.files); e.target.value = ''; }}
              className="hidden"
              id="receipt-upload"
              accept="image/*,.pdf"
            />
            
            {/* The visible "Select Files" button */}
            <label 
              htmlFor="receipt-upload"
              className="bg-zinc-800 hover:bg-zinc-700 text-white px-6 py-3 rounded-xl font-medium cursor-pointer transition-colors inline-block"
            >
              Select Files
            </label>

            {/* ONLY show the final Upload button if they actually selected files */}
            {receiptFiles.length > 0 && (
              <div className="mt-8 pt-6 border-t border-zinc-800">
                <p className="text-zinc-300 mb-4">{receiptFiles.length} file(s) ready to upload</p>
                <button 
                  onClick={() => handleUploadReceipts(receiptFiles)}
                  className="bg-indigo-600 hover:bg-indigo-500 px-8 py-3 rounded-xl text-white font-bold transition-all w-full"
                >
                  Upload and Scan
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  </div>
)}

      {/* ====================== SCAN / CROP STEP ====================== */}
      {cropQueue.length > 0 && (
        <ReceiptCropper
          file={cropQueue[0]}
          remaining={cropQueue.length}
          onDone={(croppedFile) => {
            setReceiptFiles(prev => [...prev, croppedFile]);
            setCropQueue(q => q.slice(1));
          }}
          onUseOriginal={() => {
            setReceiptFiles(prev => [...prev, cropQueue[0]]);
            setCropQueue(q => q.slice(1));
          }}
          onCancelAll={() => setCropQueue([])}
        />
      )}

      {/* ====================== ENHANCED EXTRACTION REVIEW MODAL ====================== */}
{/* ====================== HYBRID EXTRACTION REVIEW MODAL (SINGLE & BATCH) ====================== */}


{showExtractionReview && (
  <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[110] p-4 max-md:p-0">
    <div className={`bg-gray-900 rounded-2xl max-md:rounded-none border border-gray-700 max-md:border-0 w-full flex flex-col shadow-2xl ${batchData.length > 0 ? 'max-w-5xl' : 'max-w-2xl'} max-h-[90vh] max-md:max-h-[100dvh] max-md:h-[100dvh] overflow-hidden`}>

      {/* Header */}
      <div className="p-6 max-md:p-4 border-b border-gray-800 flex justify-between items-center bg-gray-900/50">
        <div>
          <h2 className="text-xl max-md:text-base font-bold text-white flex items-center gap-2">
            {batchData.length > 0 ? <Plus className="text-indigo-400" /> : <Check className="text-emerald-500" />}
            {batchData.length > 0 ? `Review Batch Upload (${batchData.length} Receipts)` : 'Review Extracted Data'}
          </h2>
          <p className="text-xs text-zinc-500 mt-1">Please verify the details before saving to project expenses.</p>
        </div>
        <button onClick={() => setShowExtractionReview(false)} className="p-2 hover:bg-gray-800 rounded-lg transition-colors">
          <X className="text-zinc-500" />
        </button>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto p-6 max-md:p-3 bg-gray-950/20">
        {batchData.length > 0 ? (
          /* ============ BATCH VIEW TABLE ============ */
          <div className="space-y-4">
            <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-800">
                <thead className="bg-gray-800/40">
                  <tr>
                    <th className="px-4 py-4 text-left w-10">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-700 bg-gray-800 text-indigo-600 focus:ring-indigo-600"
                        checked={batchData.length > 0 && selectedReceipts.length === batchData.length}
                        onChange={(e) => setSelectedReceipts(e.target.checked ? batchData.map(r => r.id) : [])}
                      />
                    </th>
                    <th className="px-4 py-4 max-md:px-2 text-left text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Store</th>
                    {/* On phones these fold into the Store cell */}
                    <th className="px-4 py-4 text-left text-[10px] font-bold text-zinc-500 uppercase tracking-wider max-md:hidden">Date / Time</th>
                    <th className="px-4 py-4 text-left text-[10px] font-bold text-zinc-500 uppercase tracking-wider max-md:hidden">Receipt #</th>
                    <th className="px-4 py-4 max-md:px-2 text-right text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {batchData.map((res) => {
                    const isSelected = selectedReceipts.includes(res.id);
                    const isEditing = editingBatchId === res.id;
                    const fld = "w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm max-md:text-base text-white";
                    const lbl = "block text-[10px] uppercase tracking-wider text-zinc-500 mb-1";
                    const sub = Number(res.subtotal) || 0, tax = Number(res.taxAmount) || 0, total = Number(res.totalAmount) || 0, charged = Number(res.chargedAmount) || 0;
                    const reconciled = (sub <= 0 || Math.abs((sub + tax) - total) <= 0.03) && (charged <= 0 || Math.abs(charged - total) <= 0.02);
                    return (
                      <React.Fragment key={res.id}>
                      <tr
                        className={`transition-colors ${res.routedAway ? '' : 'cursor-pointer'} ${isSelected ? "bg-indigo-500/5" : "hover:bg-gray-800/30"} ${isEditing ? "bg-gray-800/40" : ""} ${res.routedAway ? 'bg-amber-500/[0.04]' : ''}`}
                        onClick={() => { if (res.routedAway) return; setEditingBatchId(isEditing ? null : res.id); }}
                      >
                        <td className="px-4 py-4 max-md:px-2" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="rounded border-gray-700 bg-gray-800 text-indigo-600 focus:ring-indigo-600"
                            checked={isSelected}
                            onChange={() => {
                              setSelectedReceipts(prev => prev.includes(res.id) ? prev.filter(i => i !== res.id) : [...prev, res.id]);
                            }}
                          />
                        </td>
                        <td className="px-4 py-4 max-md:px-2">
                          <div className="text-sm font-semibold text-white flex items-center gap-1.5 max-md:flex-wrap">
                            {res.store}
                            {res.linkId && <span title="Linked to another receipt">🔗</span>}
                            {res.isReturn && <span className="text-orange-400 text-[10px] font-bold">RETURN</span>}
                            {res.routedAway && <span className="text-amber-400 text-[10px] font-bold">RE-ROUTED</span>}
                            {!res.routedAway && res.needsReview && <span title={res.reviewReason || 'Needs review'}>⚠️</span>}
                            {!res.routedAway && !res.needsReview && res.mathNote && <span className="text-zinc-500" title={res.mathNote}>ⓘ</span>}
                          </div>
                          {res.routedAway ? (
                            <div className="text-[10px] text-amber-400/90 truncate max-w-[220px]">{res.reviewReason}</div>
                          ) : (
                            <div className="text-[10px] text-zinc-500 truncate max-w-[180px]">
                              {res.storeLocation || ''}{res.poNumber ? ` · PO ${res.poNumber}` : ''}
                            </div>
                          )}
                          {/* Phones: date + receipt # fold in here since their columns are hidden */}
                          <div className="md:hidden text-[10px] text-zinc-500 mt-0.5">
                            {res.date?.split('T')[0] || ''}{res.transactionTime ? ` ${res.transactionTime}` : ''}
                            {res.isReturn
                              ? ` · ↩ ${res.originalReceiptNumber || '(unknown)'}`
                              : (res.receiptNumber ? ` · #${res.receiptNumber}` : '')}
                          </div>
                        </td>
                        <td className="px-4 py-4 max-md:hidden">
                          <div className="text-sm text-zinc-300">{res.date?.split('T')[0]}</div>
                          <div className="text-[10px] text-zinc-500">
                            {res.transactionTime || '--:--'}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm font-mono text-zinc-400 max-md:hidden">
                          {res.isReturn ? (
                            <div className="flex flex-col">
                              <span className="text-orange-300" title="Original purchase being refunded">↩ {res.originalReceiptNumber || '(unknown original)'}</span>
                              {res.receiptNumber && <span className="text-[10px] text-zinc-600">slip #{res.receiptNumber}</span>}
                              {res.splitCount > 1 && (
                                <span
                                  className="mt-0.5 inline-block w-fit text-[10px] font-semibold text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded"
                                  title={`This slip refunds ${res.splitCount} original receipts.`}
                                >
                                  {(res.splitIndex ?? 0) + 1} of {res.splitCount} · part of return #{res.receiptNumber}
                                </span>
                              )}
                            </div>
                          ) : (res.receiptNumber || 'N/A')}
                        </td>
                        <td className="px-4 py-4 max-md:px-2 text-sm text-right font-bold text-emerald-400">
                          <span className="inline-flex items-center gap-2">
                            ${Math.abs(Number(res.totalAmount || 0)).toLocaleString(undefined, {minimumFractionDigits: 2})}
                            {!res.routedAway && (isEditing ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <Pencil className="w-3.5 h-3.5 text-zinc-600" />)}
                          </span>
                        </td>
                      </tr>
                      {isEditing && (
                        <tr className="bg-gray-950/40">
                          <td colSpan={5} className="px-5 py-5 border-t border-gray-800">
                            <div className="flex justify-end mb-3">
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); viewBatchReceipt(res); }}
                                className="text-xs text-indigo-400 hover:text-indigo-300 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-indigo-500/30 hover:bg-indigo-500/10 transition-colors"
                              >
                                <FileText className="w-3.5 h-3.5" /> View receipt
                              </button>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              <div><label className={lbl}>Store</label><input className={fld} value={res.store || ''} onChange={(e) => updateBatchField(res.id, 'store', e.target.value)} /></div>
                              <div><label className={lbl}>Location</label><input className={fld} value={res.storeLocation || ''} onChange={(e) => updateBatchField(res.id, 'storeLocation', e.target.value)} placeholder="Carmichael, CA" /></div>
                              <div><label className={lbl}>PO / Job</label><input className={fld} value={res.poNumber || ''} onChange={(e) => updateBatchField(res.id, 'poNumber', e.target.value)} placeholder="0083" /></div>
                              <div><label className={lbl}>Receipt #</label><input className={fld} value={res.receiptNumber || ''} onChange={(e) => updateBatchField(res.id, 'receiptNumber', e.target.value)} /></div>
                              {res.isReturn && (
                                <div>
                                  <label className={lbl}>Original receipt # <span className="text-orange-400 normal-case">(refunds)</span></label>
                                  <input className={fld} value={res.originalReceiptNumber || ''} onChange={(e) => updateBatchField(res.id, 'originalReceiptNumber', e.target.value)} placeholder="orig receipt being returned" />
                                </div>
                              )}
                              <div><label className={lbl}>Date</label><input type="date" className={fld} value={(res.date || '').split('T')[0]} onChange={(e) => updateBatchField(res.id, 'date', e.target.value)} /></div>
                              <div><label className={lbl}>Time</label><input className={fld} value={res.transactionTime || ''} onChange={(e) => updateBatchField(res.id, 'transactionTime', e.target.value)} placeholder="HH:mm:ss" /></div>
                              <div><label className={lbl}>Subtotal</label><input type="number" step="0.01" className={fld} value={res.subtotal ?? ''} onChange={(e) => updateBatchField(res.id, 'subtotal', parseFloat(e.target.value) || 0)} /></div>
                              <div><label className={lbl}>Tax</label><input type="number" step="0.01" className={fld} value={res.taxAmount ?? ''} onChange={(e) => updateBatchField(res.id, 'taxAmount', parseFloat(e.target.value) || 0)} /></div>
                              <div><label className={lbl}>Total</label><input type="number" step="0.01" className={fld} value={res.totalAmount ?? ''} onChange={(e) => updateBatchField(res.id, 'totalAmount', parseFloat(e.target.value) || 0)} /></div>
                              <div><label className={lbl}>Charged (USD$)</label><input type="number" step="0.01" className={fld} value={res.chargedAmount ?? ''} onChange={(e) => updateBatchField(res.id, 'chargedAmount', parseFloat(e.target.value) || 0)} /></div>
                            </div>
                            {/* live reconciliation readout */}
                            <div className={`mt-3 text-xs flex flex-wrap items-center gap-x-4 gap-y-1 ${reconciled ? 'text-emerald-400' : 'text-amber-400'}`}>
                              <span>{reconciled ? '✓ reconciles' : '⚠ does not reconcile'}</span>
                              <span className="text-zinc-500">subtotal + tax = ${(sub + tax).toFixed(2)}</span>
                              <span className="text-zinc-500">total = ${total.toFixed(2)}</span>
                              {charged > 0 && <span className="text-zinc-500">charged = ${charged.toFixed(2)}</span>}
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-zinc-500 text-center italic">
              Tip: Uncheck any receipts that didn't extract correctly and use "Delete Selected" to clear them.
            </p>
          </div>
        ) : (
          /* ============ SINGLE VIEW (Your existing detail card) ============ */
          <div className="space-y-6">
            <div className="p-4 bg-gray-800/50 rounded-xl border border-gray-700">
              <label className="text-xs text-zinc-500 uppercase font-bold">Store & Address</label>
              <p className="font-semibold text-lg text-white">{extractedData?.store}</p>
              <p className="text-sm text-zinc-400">{extractedData?.storeAddress || 'Address not detected'}</p>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs text-zinc-500 uppercase font-bold">PO Number / Job</label>
                <p className="font-medium text-emerald-400">{extractedData?.poNumber || 'None'}</p>
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase font-bold">Receipt / Invoice #</label>
                <p className="font-medium text-white font-mono">{extractedData?.receiptNumber || 'None'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs text-zinc-500 uppercase font-bold">Date</label>
                <p className="font-medium text-white">{extractedData?.date?.split('T')[0]}</p>
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase font-bold">Time</label>
                <p className="font-medium text-white">
                  {(() => {
                    if (!extractedData?.date || !extractedData.date.includes('T')) return '--:--';
                    const [hours, minutes] = extractedData.date.split('T')[1].split(':');
                    const h = parseInt(hours);
                    return `${h % 12 || 12}:${minutes} ${h >= 12 ? 'pm' : 'am'}`;
                  })()}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-6 py-6 border-y border-gray-800/50">
              <div><label className="text-xs text-zinc-500 block mb-1">Subtotal</label><p className="font-bold text-white">${extractedData?.subtotal}</p></div>
              <div><label className="text-xs text-zinc-500 block mb-1">Tax</label><p className="font-bold text-red-400">${extractedData?.taxAmount}</p></div>
              <div><label className="text-xs text-zinc-500 block mb-1">Total</label><p className="font-bold text-emerald-400 text-xl">${extractedData?.totalAmount}</p></div>
            </div>

            {extractedData?.items?.length > 0 && (
              <div className="bg-gray-800/30 rounded-xl border border-gray-800 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-800/50">
                    <tr>
                      <th className="px-4 py-2 text-left text-zinc-500">Description</th>
                      <th className="px-4 py-2 text-center text-zinc-500">Qty</th>
                      <th className="px-4 py-2 text-right text-zinc-500">Price</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {extractedData.items.map((item, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 text-zinc-300">{item.name}</td>
                        <td className="px-4 py-2 text-center text-zinc-500">{item.quantity}</td>
                        <td className="px-4 py-2 text-right text-white font-medium">${item.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer Actions — stacks into full-width buttons on phones */}
      <div className="p-6 max-md:p-4 border-t border-gray-800 bg-gray-900/80 flex items-center gap-4 max-md:flex-col-reverse max-md:items-stretch max-md:gap-2.5">
        {batchData.length > 0 ? (
          <>
            <button
              onClick={async () => {
                const token = localStorage.getItem('token');
                // Project-level delete is authoritative: for any selected re-routed
                // stub whose real item went to the Unallocated bucket, also remove
                // that counterpart from the Ledger so it doesn't linger there.
                const removing = batchData.filter(r => selectedReceipts.includes(r.id));
                await Promise.all(
                  removing
                    .filter(r => r.targetUnallocated && r.targetItemId)
                    .map(r => axios.delete(`${API_BASE}/expenses/unallocated-item/${r.targetItemId}`, { headers: { Authorization: token } }).catch(() => {}))
                );

                const remaining = batchData.filter(r => !selectedReceipts.includes(r.id));

                if (remaining.length === 0) {
                  // 1. Delete the batch from the database so the pulsing button stops
                  if (currentPendingId) {
                    try {
                      await axios.delete(`${API_BASE}/expenses/pending/${currentPendingId}`, {
                        headers: { Authorization: token }
                      });
                      // Instantly clear it from local polling state
                      setPendingReceipts(prev => prev.filter(p => p._id !== currentPendingId));
                      setCurrentPendingId(null);
                    } catch (err) {
                      console.error("Failed to delete pending batch:", err);
                    }
                  }
                  
                  // 2. Clear state and switch back to the upload modal
                  setBatchData([]);
                  setSelectedReceipts([]);
                  setShowExtractionReview(false);
                  setShowAddReceiptModal(false);
                } else {
                  // If only deleting some of the receipts, just update the local UI state
                  setBatchData(remaining);
                  setSelectedReceipts(remaining.map(r => r.id));
                }
                // Tell the notification bell to refresh now (deleting these may
                // have resolved their "needs allocation" alert) instead of
                // waiting for its next 8s poll.
                window.dispatchEvent(new Event('notifications:refresh'));
              }}
              disabled={selectedReceipts.length === 0}
              className="px-5 py-3 bg-red-600/10 text-red-500 hover:bg-red-600/20 rounded-xl font-bold transition-all disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2 max-md:justify-center"
            >
              <Trash2 size={18} /> Delete Selected
            </button>
            {/* Grand total of everything in this batch (returns show as a credit) */}
            <div className="flex-1 flex items-center gap-2 max-md:justify-center max-md:py-1">
              {(() => {
                const gt = batchData.reduce((s, r) => s + (Number(r.totalAmount) || 0), 0);
                const neg = gt < 0;
                return (
                  <>
                    <span className="text-[11px] text-zinc-500 font-bold uppercase tracking-wide">
                      {batchData.length} receipt{batchData.length === 1 ? '' : 's'} · total
                    </span>
                    <span className={`text-lg font-bold ${neg ? 'text-amber-400' : 'text-emerald-300'}`}>
                      {neg ? '−' : ''}${Math.abs(gt).toLocaleString(undefined, { minimumFractionDigits: 2 })}{neg ? ' credit' : ''}
                    </span>
                  </>
                );
              })()}
            </div>
            <button onClick={() => setShowExtractionReview(false)} className="text-zinc-500 font-medium px-4 max-md:py-2">Cancel</button>
            <button
              onClick={handleSaveBatch}
              disabled={selectedReceipts.length === 0 || isSaving}
              className="px-8 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold shadow-lg shadow-emerald-900/20 transition-all disabled:opacity-50 flex items-center justify-center gap-2 min-w-[200px] max-md:min-w-0"
            >
              {isSaving ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving to Dropbox...
                </>
              ) : (() => {
                // Count only real (non re-route-stub) selected receipts.
                const selectedReal = batchData.filter(r => selectedReceipts.includes(r.id) && !r.routedAway).length;
                const selectedStubs = batchData.filter(r => selectedReceipts.includes(r.id) && r.routedAway).length;
                if (selectedReal === 0 && selectedStubs > 0) return 'Acknowledge Re-route';
                return `Save ${selectedReal} Expense${selectedReal === 1 ? '' : 's'}`;
              })()}
            </button>
          </>
        ) : (
          <>
            <button onClick={() => setShowExtractionReview(false)} className="flex-1 py-3 bg-gray-800 text-white rounded-xl font-bold">Discard</button>
            <button onClick={handleSaveExtractedExpense} className="flex-1 py-3 bg-emerald-600 text-white rounded-xl font-bold">Save Expense</button>
          </>
        )}
      </div>
    </div>
  </div>
)}
    </div>
  );
}

export default ExpenseTab;